"use client";

import React, { useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
const reassurances = [{
  heading: "Every consultation is in English",
  body: "All three neurosurgeons consult international patients directly in English. From your first online call to your pre-operative briefing, you speak with your surgeon, not through an intermediary."
}, {
  heading: "Your recovery is supervised from arrival to departure",
  body: "From the moment you arrive in Kaunas, your recovery is managed by our team. After surgery, you spend 1 to 2 nights at the clinic, then 12 to 20 days at Egle Rehabilitation center in Birstonas under supervised rehabilitation. Your surgeon confirms you are safe to fly before you leave. Follow-up consultations are available online after you return to Ireland."
}, {
  heading: "We prepare your HSE paperwork",
  body: "We prepare your Discharge Summary and Operative Report in the format required for your HSE Cross-Border Directive application. Our specialist walks you through the prior notification process before you travel."
}, {
  heading: "Your GP receives full clinical documentation",
  body: "After your procedure, we issue a complete Discharge Summary and Operative Report. Your GP receives the same documentation they would expect from any European specialist."
}, {
  heading: "One person is responsible for your entire stay",
  body: "Your patient coordinator arranges your hotel, pre-operative tests, all medical appointments and every transfer in Lithuania. You have one phone number from the moment you land to the moment you fly home. You do not navigate a foreign country alone after surgery."
}];
const timelineSteps = [{
  id: 1,
  tag: "ONLINE",
  title: "First contact",
  body: "Fill in our enquiry form, send us an email or call us. We get back to you, listen to your situation and assess whether we can help.",
  duration: "Within 1 business day",
  facility: "Online",
  note: "No commitment required"
}, {
  id: 2,
  tag: "ONLINE",
  title: "Consultation with your surgeon",
  body: "You send your MRI scans. Your neurosurgeon reviews them and consults with you remotely. He confirms whether surgery is needed and outlines your treatment plan.",
  duration: "1 hour",
  facility: "Video call",
  note: "Consultation fee: €180"
}, {
  id: 3,
  tag: "ONLINE",
  title: "HSE prior notification",
  body: "If surgery is recommended, our specialist explains the HSE reimbursement process and helps you submit a prior notification. HSE responds within 6 weeks to confirm eligibility. We prepare for your arrival in the meantime.",
  duration: "6 weeks",
  facility: "HSE Kilkenny",
  note: "Recommended before travel"
}, {
  id: 4,
  tag: "KAUNAS",
  title: "Arrival and pre-operative assessment",
  body: "You fly to Kaunas. Your patient coordinator arranges your hotel and all transfers from this point. The following day you meet your neurosurgeon in person, complete blood tests and ECG, and rest before your procedure. Surgery is never performed on the day of arrival.",
  duration: "1 day",
  facility: "Clinic Agatas",
  note: "Your patient coordinator handles all logistics from arrival"
}, {
  id: 5,
  tag: "KAUNAS",
  title: "Surgery and post-operative care",
  body: "Your procedure is performed by your neurosurgeon. Afterwards, you stay at the clinic for 1 or 2 nights under 24/7 supervision. The following day you receive a neurosurgeon follow-up, pain specialist consultation, clinical psychologist session and first physiotherapy.",
  duration: "1 to 2 nights",
  facility: "Clinic Agatas",
  note: "All post-op care included"
}, {
  id: 6,
  tag: "BIRSTONAS",
  title: "Rehabilitation and home",
  body: "Your patient coordinator arranges your transfer to Egle Rehabilitation center for 12 to 20 days of supervised recovery. Private room, five meals a day and an individual rehabilitation programme. On the day of your flight, you return to Clinic Agatas for a final follow-up with your neurosurgeon, who confirms you are safe to fly home.",
  duration: "12 to 20 days",
  facility: "Egle Rehabilitation center",
  note: "Final follow-up at Clinic Agatas included"
}];
const egleProcedures = ["Individual physiotherapy", "Polarised light therapy", "Electrotherapy: muscle electrostimulation, TENS, interferential currents", "Therapeutic massage", "Local peloid therapy (mud applications)"];
const eglePhotos = [{
  src: "https://painreliefabroad.ie/wp-content/uploads/2026/06/fasadas-1680-x-945-1.jpg?v=xj4f0cou78f",
  alt: "Egle Rehabilitation center exterior"
}, {
  src: "https://painreliefabroad.ie/wp-content/uploads/2026/06/fasadas-2-1.jpg?v=3ke8ai11z5z",
  alt: "Egle Rehabilitation center grounds"
}, {
  src: "https://painreliefabroad.ie/wp-content/uploads/2026/06/Gydykla-1-scaled.jpg?v=9fypzg02834",
  alt: "Egle Rehabilitation center interior"
}, {
  src: "https://painreliefabroad.ie/wp-content/uploads/2026/06/Baseinas-1.jpg?v=05jagzym0i84",
  alt: "Egle Rehabilitation center pool"
}, {
  src: "https://painreliefabroad.ie/wp-content/uploads/2026/06/DS_2165-1.jpg?v=zmiliy3o6tc",
  alt: "Private room at Egle Rehabilitation center"
}, {
  src: "https://painreliefabroad.ie/wp-content/uploads/2026/06/DS_0992-1-scaled.jpg?v=uidzmca0ly",
  alt: "Physiotherapy at Egle Rehabilitation center"
}];
function Lightbox({
  photo,
  onClose
}: {
  photo: {
    src: string;
    alt: string;
  };
  onClose: () => void;
}) {
  return <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-4xl w-full max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white hover:text-[#65d19d] transition-colors">
          <X className="w-8 h-8" />
        </button>
        <img src={photo.src} alt={photo.alt} className="w-full h-full object-contain rounded-lg max-h-[85vh]" />
      </div>
    </div>;
}
function ReassuranceSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="bg-[#eefaf4] py-12 md:py-20 border-b border-[#a9e5c9]">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] mb-3">Before you decide anything</h2>
            <p className="font-default text-muted-foreground text-base max-w-2xl mx-auto">These are the questions we hear most often. Here are straight answers.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-10">
            {reassurances.map((item, i) => <div key={i} className={cn("bg-white rounded-lg border border-[#a9e5c9] p-5 md:p-6 flex flex-col gap-3", i === reassurances.length - 1 && reassurances.length % 2 !== 0 ? "md:col-span-2" : "")}>
                <div className="flex items-start gap-3">
                  <div className="bg-[#eefaf4] rounded-full p-1 shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-[#65d19d]" strokeWidth={3} />
                  </div>
                  <h3 className="font-default font-bold text-[#264256] text-base leading-snug">{item.heading}</h3>
                </div>
                <p className="font-default text-muted-foreground text-sm leading-relaxed pl-7">{item.body}</p>
              </div>)}
          </div>
          <div className="flex justify-center">
            <Button asChild variant="outline" className="rounded-lg border-2 border-[#264256] text-[#264256] hover:bg-[#264256] hover:text-white h-11 md:h-12 px-6 md:px-8 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300">
              <a href="/our-specialists#neurosurgeons">Meet our medical team</a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}
function TimelineItem({
  step,
  index
}: {
  step: typeof timelineSteps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const isLeft = index % 2 === 0;
  return <div ref={ref} className={cn("relative flex flex-col md:flex-row items-center gap-6 md:gap-16 transition-all duration-1000 ease-out mb-4", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
      <div className="absolute left-6 md:left-1/2 top-4 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#65d19d] flex items-center justify-center z-10 shadow-lg border-4 border-[#264256]">
        <span className="text-[#264256] font-default font-bold text-base md:text-lg">{step.id}</span>
      </div>
      <div className={cn("w-full pl-16 md:pl-0 md:w-[45%]", isLeft ? "md:mr-auto md:text-right" : "md:ml-auto md:order-2")}>
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-white/20">
          <div className={cn("flex mb-3", isLeft ? "justify-start md:justify-end" : "justify-start")}>
            <span className="text-[#65d19d] text-xs font-default font-bold uppercase tracking-wider bg-[#65d19d]/10 px-3 py-1 rounded-full">{step.tag}</span>
          </div>
          <h3 className="text-white text-lg md:text-xl font-default font-bold mb-2 md:mb-3">{step.title}</h3>
          <p className="text-white/85 text-sm font-default leading-relaxed">{step.body}</p>
        </div>
      </div>
      <div className={cn("w-full pl-16 md:pl-0 md:w-[45%] flex flex-col gap-2 py-2", isLeft ? "md:ml-auto text-left" : "md:mr-auto md:order-1 md:text-right")}>
        <div className="flex flex-col">
          <span className="text-[#65d19d] text-xs font-bold uppercase tracking-widest">Duration</span>
          <span className="text-white/90 font-default text-sm">{step.duration}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#65d19d] text-xs font-bold uppercase tracking-widest">Location</span>
          <span className="text-white/90 font-default text-sm">{step.facility}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#65d19d] text-xs font-bold uppercase tracking-widest">Key note</span>
          <span className="text-white/70 font-default text-sm italic">{step.note}</span>
        </div>
      </div>
    </div>;
}
function EgleSanatoriumSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [lightboxPhoto, setLightboxPhoto] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  return <section id="egle-sanatorium" className="bg-background py-12 md:py-20">
      {lightboxPhoto && <Lightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} />}
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="flex flex-col lg:flex-row gap-10 md:gap-12 items-stretch">
            <div className="w-full lg:w-[50%] flex flex-col gap-4 md:gap-5">
              <span className="font-default text-[12px] text-[#65d19d] uppercase tracking-widest font-semibold">Rehabilitation Partner</span>
              <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] leading-tight">Egle Rehabilitation center, Birstonas</h2>
              <p className="font-default text-muted-foreground text-base leading-relaxed">A licensed medical rehabilitation centre 40 minutes from Clinic Agatas, set in a quiet resort town surrounded by forests and the Nemunas river. Each patient receives a private room, five meals a day and an individual rehabilitation programme developed specifically for post-operative recovery.</p>
              <p className="font-default text-muted-foreground text-base leading-relaxed">On arrival, a rehabilitation physician assesses you and designs your individual programme. For the first two days you receive medical supervision and initial treatment. From day three, your full rehabilitation schedule begins, on average around five procedures per day, including physiotherapy.&nbsp;</p>
              <div className="bg-[#eefaf4] rounded-lg border border-[#a9e5c9] p-5 md:p-6 flex-1">
                <h3 className="font-default font-bold text-[#264256] text-sm uppercase tracking-wider mb-4">Procedures typically prescribed after spinal surgery</h3>
                <ul className="flex flex-col gap-2">
                  {egleProcedures.map((item, i) => <li key={i} className="flex items-start gap-3">
                      <div className="bg-white rounded-full p-1 shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#65d19d]" strokeWidth={3} />
                      </div>
                      <span className="font-default text-[#264256] text-sm leading-relaxed">{item}</span>
                    </li>)}
                </ul>
                <p className="font-default text-muted-foreground text-xs mt-4">76 medical and therapeutic procedures available in total. Your programme is determined by your rehabilitation physician on arrival.</p>
                <div className="mt-4">
                  <Button asChild variant="outline" className="rounded-lg border-2 border-[#264256] text-[#264256] hover:bg-[#264256] hover:text-white h-10 px-6 text-xs uppercase tracking-widest font-bold font-default transition-all duration-300">
                    <a href="https://sanatorija.lt/en/birstonas/" target="_blank" rel="noopener noreferrer">More information</a>
                  </Button>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-[50%] grid grid-cols-2 gap-3" style={{
            gridTemplateRows: "repeat(3, 1fr)"
          }}>
              {eglePhotos.map((photo, i) => <div key={i} className="relative overflow-hidden rounded-lg bg-[#eefaf4] border border-[#a9e5c9] cursor-pointer group min-h-[120px] md:min-h-0" onClick={() => setLightboxPhoto(photo)}>
                  <img src={photo.src} alt={photo.alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <span className="text-white text-xs font-default uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300">View</span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export default function YourJourney() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: lineRef,
    isVisible: lineVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section id="your-journey" className="bg-background">
      <ReassuranceSection />

      <div className="bg-[#264256] py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-6xl mx-auto relative">
          <div ref={headerRef} className={cn("text-center mb-12 md:mb-24 transition-all duration-1000 ease-out", headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            <h2 className="font-default font-bold text-3xl md:text-5xl lg:text-[60px] leading-tight text-white max-w-4xl mx-auto">
              Your journey from first contact to recovery.
            </h2>
          </div>
          <div className="relative pb-12 md:pb-0">
            <div ref={lineRef} className={cn("absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2 border-l-2 border-dashed border-[#65d19d]/60 transition-all duration-[1500ms] ease-in-out origin-top", lineVisible ? "scale-y-100 opacity-100" : "scale-y-0 opacity-0")} />
            <div className="relative z-10 flex flex-col gap-4">
              {timelineSteps.map((step, index) => <TimelineItem key={step.id} step={step} index={index} />)}
            </div>
          </div>
          <div className="mt-12 md:mt-16 max-w-2xl mx-auto text-center">
            <p className="font-default text-white/50 text-xs leading-relaxed">
              This pathway applies to patients using the HSE Cross-Border Directive for reimbursement. If you are travelling with private health insurance, the process is simpler — the HSE prior notification step does not apply and your journey can be arranged more quickly. Contact us to discuss your specific situation.
            </p>
          </div>
        </div>
      </div>

      <EgleSanatoriumSection />

      <section className="bg-background py-10 md:py-16">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="bg-[#264256] rounded-2xl px-6 md:px-10 py-10 md:py-14 text-center flex flex-col items-center gap-6 md:gap-8">
            <div className="flex flex-col gap-3 md:gap-4">
              <h2 className="font-default font-bold text-2xl md:text-4xl text-white leading-tight">The longer you wait, <br />the harder it gets.</h2>
              <p className="font-default text-white/80 text-sm md:text-base leading-relaxed max-w-xl mx-auto">Nerve compression from disc herniation does not improve with time. An online consultation with one of our neurosurgeons costs €180 and commits you to nothing.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="rounded-lg bg-[#65d19d] text-[#264256] hover:bg-[#65d19d]/90 h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300 shadow-lg">
                <Link to="/#request-your-consultation">Book a Consultation</Link>
              </Button>
              <Button asChild className="rounded-lg bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#264256] h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300">
                <a href="/our-specialists#neurosurgeons">Meet our surgeons</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </section>;
}